import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AdSidebar from '../components/AdSidebar';
import { BookOpen, Lock, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_BADGE = {
  A1: 'bg-green-100 text-green-800 border-green-300',
  A2: 'bg-teal-100 text-teal-800 border-teal-300',
  B1: 'bg-blue-100 text-blue-800 border-blue-300',
  B2: 'bg-blue-100 text-blue-800 border-blue-300',
  C1: 'bg-sky-100 text-sky-800 border-sky-300',
  C2: 'bg-cyan-100 text-cyan-800 border-cyan-300',
};

const GrammarPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState(searchParams.get('level') || 'A1');
  const urlLevel = searchParams.get('level');

  useEffect(() => {
    setSelectedLevel(urlLevel || 'A1');
  }, [urlLevel]);
  const { user } = useAuth();

  const levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

  useEffect(() => {
    fetchTopics();
  }, [selectedLevel]);

  const fetchTopics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/grammar/topics?level=${selectedLevel}`);
      if (response.ok) {
        const data = await response.json();
        setTopics(data);
      }
    } catch (error) {
      console.error('Failed to fetch topics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLevelChange = (level) => {
    setSelectedLevel(level);
    setSearchParams({ level });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white" data-testid="grammar-page">
      <Navbar />

      <main className="flex-1 py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          {/* Hero */}
          <div className="mb-10 text-center">
            <div className="inline-flex items-center gap-2 bg-babyblue-100 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <BookOpen className="w-4 h-4" />
              CEFR A1 – C2
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-babyblue-800 mb-3" data-testid="grammar-page-title">
              Grammar Lessons
            </h1>
            <p className="text-lg text-babyblue-600 max-w-2xl mx-auto">
              Master German grammar with structured lessons and exercises — from beginner to mastery.
            </p>
          </div>

          {/* Level Filter */}
          <div className="mb-10">
            <div className="flex flex-wrap gap-2 justify-center">
              {levels.map((level) => (
                <button
                  key={level}
                  onClick={() => handleLevelChange(level)}
                  className={`px-5 py-2 rounded-full font-semibold text-sm transition-all border-2 ${
                    selectedLevel === level
                      ? 'bg-primary text-white border-primary shadow-md'
                      : 'bg-white text-babyblue-700 border-babyblue-200 hover:border-primary hover:bg-babyblue-50'
                  }`}
                  data-testid={`level-filter-${level.toLowerCase()}`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          {/* Topics List */}
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="spinner"></div>
            </div>
          ) : topics.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-babyblue-200">
              <p className="text-babyblue-600">No topics yet for {selectedLevel}.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {topics.map((topic) => (
                <Link
                  key={topic.topic_id}
                  to={`/grammar/${topic.topic_id}`}
                  className="bg-white rounded-2xl shadow-sm border border-babyblue-200 p-6 hover:border-primary hover:shadow-md transition-all block group"
                  data-testid={`topic-${topic.topic_id}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <BookOpen className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${LEVEL_BADGE[topic.level]}`}>
                          {topic.level}
                        </span>
                        {topic.premium && (
                          <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1">
                            <Lock className="w-3 h-3" />
                            Premium
                          </span>
                        )}
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-babyblue-800 group-hover:text-primary transition-colors">
                        {topic.title}
                      </h3>
                      <p className="text-sm text-babyblue-600 mt-1 line-clamp-2">
                        {topic.explanation.substring(0, 160).replace(/<[^>]*>/g, '').trim()}…
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-babyblue-400 group-hover:text-primary flex-shrink-0 transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Ad space */}
          <div className="ad-container mt-10" data-testid="ad-placeholder">
            <p>Advertisement Space</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default GrammarPage;
